// Portage TypeScript de feuille/Generateur.gs (fonctions genererSeances,
// calculerRPEGenerateur, formaterRecuperationGenerateur et formatTemps du
// meme fichier - RENOMMEES ici avec le suffixe "Generateur" pour ne pas
// entrer en collision avec les fonctions de meme nom, mais de logique
// differente, de seanceEngine.ts qui sert la page Construction).
//
// Reutilise la meme table de zones (ALLURE_TABLE) que Construction,
// seule source de verite portee depuis feuille/Allures_repetitions.xlsx.
//
// A NOTER (ecart assume avec le commentaire de tete de genererSeances()
// dans Generateur.gs, qui affirme utiliser "la recuperation saisie en
// C5") : le corps de la fonction ne lit JAMAIS cette cellule C5, et
// parseRecuperationGenerateur() (le parseur dedie a cette saisie) n'est
// appele nulle part dans le fichier - la recuperation utilisee est
// TOUJOURS effortS * ratioMax de la zone. C'est ce comportement reel,
// et non celui du commentaire (perime) ni le parseur (mort), qui est
// porte ici a l'identique.

import { ALLURE_TABLE, type ZoneRow } from '../Construction/allureTable.ts';

// Paliers de distance testes pour chaque zone (Generateur.gs, tableau
// "distances").
const DISTANCES: number[] = [
  200, 300, 400, 500, 600, 800, 1000, 1200, 1500, 1600, 2000, 2500, 3000, 4000, 5000, 6000, 8000, 10000,
];

/**
 * Estimation du RPE (1-10) d'une repetition generee, independante de
 * celle de seanceEngine.ts (Code.gs / ANALYSE_SEANCE) : memes 4
 * composantes (intensite, duree, reps, recuperation) mais paliers et
 * ponderations differents, fideles a calculerRPEGenerateur.
 */
export function calculerRPEGenerateur(pctVMA: number, effortS: number, reps: number, recuperationS: number): number {
  let scoreIntensite: number;
  if (pctVMA < 0.70) scoreIntensite = 2;
  else if (pctVMA < 0.75) scoreIntensite = 3;
  else if (pctVMA < 0.80) scoreIntensite = 4;
  else if (pctVMA < 0.85) scoreIntensite = 5;
  else if (pctVMA < 0.90) scoreIntensite = 6;
  else if (pctVMA < 0.95) scoreIntensite = 7;
  else if (pctVMA < 1.00) scoreIntensite = 8;
  else if (pctVMA < 1.05) scoreIntensite = 9;
  else scoreIntensite = 10;

  const effortMin = effortS / 60;
  let scoreDuree: number;
  if (effortMin <= 1) scoreDuree = 0;
  else if (effortMin <= 3) scoreDuree = 0.25;
  else if (effortMin <= 5) scoreDuree = 0.50;
  else if (effortMin <= 10) scoreDuree = 0.75;
  else if (effortMin <= 15) scoreDuree = 1.00;
  else scoreDuree = 1.25;

  let scoreReps: number;
  if (reps <= 3) scoreReps = 0;
  else if (reps <= 5) scoreReps = 0.25;
  else if (reps <= 7) scoreReps = 0.50;
  else if (reps <= 10) scoreReps = 0.75;
  else scoreReps = 1.00;

  const ratioRecup = recuperationS / effortS;
  let scoreRecuperation: number;
  if (ratioRecup >= 1.50) scoreRecuperation = -0.75;
  else if (ratioRecup >= 1.00) scoreRecuperation = -0.50;
  else if (ratioRecup >= 0.75) scoreRecuperation = -0.25;
  else if (ratioRecup >= 0.50) scoreRecuperation = 0;
  else if (ratioRecup >= 0.30) scoreRecuperation = 0.50;
  else scoreRecuperation = 1.00;

  let rpe = scoreIntensite + scoreDuree + scoreReps + scoreRecuperation;
  rpe = Math.max(1, Math.min(10, rpe));
  return Math.round(rpe);
}

// Formatage d'une duree d'effort/temps total, ex: 45 -> "0'45"", 180 -> "3'".
// (Convention avec guillemet droit simple pour les secondes, propre a
// Generateur.gs - distincte du "MM:SS" de seanceEngine.ts et du "''" de
// formaterRecuperationGenerateur ci-dessous.)
export function formaterTempsGenerateur(secondes: number): string {
  const s = Math.round(secondes);
  const minutes = Math.floor(s / 60);
  const sec = s % 60;
  if (sec === 0) return `${minutes}'`;
  return `${minutes}'${sec}"`;
}

// Formatage d'une recuperation, ex: 90 -> "1'30''", 180 -> "3'".
// (Convention a deux apostrophes pour les secondes, comme le reste de
// la notation de recuperation - cf parseRecuperation dans seanceEngine.ts.)
export function formaterRecuperationGenerateur(secondes: number): string {
  const s = Math.round(secondes);
  if (s % 60 === 0) return `${s / 60}'`;
  const minutes = Math.floor(s / 60);
  const sec = s % 60;
  return `${minutes}'${sec}''`;
}

export type SeanceGeneree = {
  nomZone: string;
  code: string;
  notation: string;
  nbReps: number;
  distanceM: number;
  // Valeurs brutes (secondes) en plus des chaines deja formatees, pour
  // permettre un tri numerique correct dans un tableau (DataGrid) plutot
  // qu'un tri alphabetique sur "4'14""/"27'4"".
  effortS: number;
  effortFormatted: string;
  recuperationS: number;
  recuperationFormatted: string;
  tempsTotalS: number;
  tempsTotalFormatted: string;
  distanceTotaleM: number;
  rpe: number;
};

/**
 * Genere toutes les seances "Nx<distance>m@<zone>-R<recup>" compatibles
 * avec la VMA donnee et les contraintes (reps/volume/distance/ratio de
 * recup) de chaque zone de la table - portage fidele de genererSeances().
 */
export function genererSeances(vma: number, table: ZoneRow[] = ALLURE_TABLE): SeanceGeneree[] {
  const resultats: SeanceGeneree[] = [];

  for (const zone of table) {
    const nom = zone.nom;
    const pctVMA = zone.pctVMA;
    const code = zone.code;
    const ratioMin = zone.ratioMin;
    const ratioMax = zone.ratioMax;
    const repsMin = zone.repsMin;
    const repsMax = zone.repsMax;
    const volumeMin = zone.volMin;
    const volumeMax = zone.volMax;
    const distanceMin = zone.distMin;
    const distanceMax = zone.distMax;

    // Zone non configuree (ex: RECUP, dont les bornes sont volontairement
    // NaN dans allureTable.ts - elle n'est jamais une allure cible de
    // seance generee) : ignoree, comme dans Generateur.gs.
    if (!nom || isNaN(pctVMA) || isNaN(repsMin) || isNaN(repsMax) || isNaN(volumeMin) || isNaN(volumeMax) || isNaN(distanceMin) || isNaN(distanceMax)) {
      continue;
    }

    const vitesseKmh = vma * pctVMA;
    const vitesseMs = (vitesseKmh * 1000) / 3600;

    for (const distance of DISTANCES) {
      if (distance < distanceMin || distance > distanceMax) continue;

      const effortS = distance / vitesseMs;
      const recuperationS = effortS * ratioMax;
      const ratio = recuperationS / effortS;
      if (ratio < ratioMin || ratio > ratioMax) continue;

      for (let reps = Math.ceil(repsMin); reps <= Math.floor(repsMax); reps++) {
        const volume = distance * reps;
        if (volume < volumeMin || volume > volumeMax) continue;

        const tempsTotalS = reps * effortS + (reps - 1) * recuperationS;
        const notation = `${reps}x${distance}m@${code}-R${formaterRecuperationGenerateur(recuperationS)}`;
        const rpe = calculerRPEGenerateur(pctVMA, effortS, reps, recuperationS);

        resultats.push({
          nomZone: nom,
          code,
          notation,
          nbReps: reps,
          distanceM: distance,
          effortS,
          effortFormatted: formaterTempsGenerateur(effortS),
          recuperationS,
          recuperationFormatted: formaterRecuperationGenerateur(recuperationS),
          tempsTotalS,
          tempsTotalFormatted: formaterTempsGenerateur(tempsTotalS),
          distanceTotaleM: Math.round(volume),
          rpe,
        });
      }
    }
  }

  // Tri : nom de zone, puis distance, puis nombre de reps (identique a
  // genererSeances()).
  resultats.sort((a, b) => {
    if (a.nomZone < b.nomZone) return -1;
    if (a.nomZone > b.nomZone) return 1;
    if (a.distanceM !== b.distanceM) return a.distanceM - b.distanceM;
    return a.nbReps - b.nbReps;
  });

  return resultats;
}

// Couleurs de fond/texte par RPE (appliquerCouleursRPE) : vert -> ecarlate.
export function couleurRPE(rpe: number): { background: string; color: string } {
  if (rpe <= 2) return { background: '#63BE7B', color: '#FFFFFF' };
  if (rpe === 3) return { background: '#A8D76F', color: '#000000' };
  if (rpe === 4) return { background: '#D9E85B', color: '#000000' };
  if (rpe === 5) return { background: '#FFD966', color: '#000000' };
  if (rpe === 6) return { background: '#F6B26B', color: '#000000' };
  if (rpe === 7) return { background: '#E69138', color: '#FFFFFF' };
  if (rpe === 8) return { background: '#E06666', color: '#FFFFFF' };
  if (rpe === 9) return { background: '#CC0000', color: '#FFFFFF' };
  return { background: '#990000', color: '#FFFFFF' };
}
